import { OpenClosedStates } from '@chiroup/core/enums/OpenClosedStates.enum';
import { Dialog, Transition } from '@headlessui/react';
import { X as XMarkIcon } from 'lucide-react';
import React, { Fragment } from 'react';

type Props = {
  title: string;
  slideOverState: OpenClosedStates;
  updateSlideOverState: (val: OpenClosedStates) => void;
  buttons: React.ReactNode;
  children: React.ReactNode;
};

const SlideOver: React.FC<Props> = ({
  title,
  slideOverState,
  updateSlideOverState,
  buttons,
  children,
}) => {
  return (
    <Transition.Root show={slideOverState === OpenClosedStates.Open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        onClose={() => updateSlideOverState(OpenClosedStates.Closed)}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-500"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full sm:pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-xl">
                  <div className="flex h-full flex-col overflow-y-scroll bg-white py-6 shadow-xl">
                    <div className="px-4 sm:px-6">
                      <div className="flex items-start justify-between">
                        <Dialog.Title className="text-lg font-medium text-gray-900">
                          {title}
                        </Dialog.Title>
                        <div className="ml-3 flex h-7 items-center">
                          <button
                            type="button"
                            className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                            onClick={() => updateSlideOverState(OpenClosedStates.Closed)}
                          >
                            <span className="sr-only">Close panel</span>
                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="relative mt-6 flex-1 px-4 sm:px-6">{children}</div>
                    <div className="shrink-0 px-4 border-t border-gray-300 dark:border-dark-gray-600 py-5 sm:px-6 dark:bg-dark-gray-700">
                      <div className="space-x-3 flex justify-end">{buttons}</div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default SlideOver;
